import { useCallback, useEffect, useRef } from 'react';
import { FieldValues, UseFormProps, UseFormReturn, useForm as useRHFForm } from 'react-hook-form';
import { registerDevForm } from './dev-form-registry';
import { useUnsavedChangesGuard } from './use-unsaved-changes-guard';

type UnsavedChangesGuardOptions = {
  message?: string;
};

/**
 * Validate on the first blur, then on every change after that.
 *
 * react-hook-form's own default is `onSubmit`, which is why a form could refuse a submit while
 * showing nothing about which field was wrong — 26 call sites across all four apps inherited it.
 * `onTouched` is the least aggressive mode that still gives change-time feedback: it never fires
 * before the user has left a field, so an empty required field is not marked wrong three
 * characters into typing, but once a field has been visited its error tracks what is in it.
 *
 * `reValidateMode` stays react-hook-form's `onChange`, so after a submit attempt every field
 * revalidates as it is corrected regardless of whether it was touched.
 */
export const DEFAULT_VALIDATION_MODE = 'onTouched' as const;

type UseFormWithGuardProps<
  TFieldValues extends FieldValues,
  TContext,
  TTransformedValues,
> = UseFormProps<TFieldValues, TContext, TTransformedValues> & {
  /**
   * Configures the unsaved changes guard that automatically blocks navigation
   * and page reload when the form has dirty fields.
   *
   * Set to `false` to opt out of the guard entirely.
   * Omit or pass an object with an optional `message` to use the default behaviour.
   */
  unsavedChangesGuard?: UnsavedChangesGuardOptions | false;
};

export function useForm<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues,
>(
  props?: UseFormWithGuardProps<TFieldValues, TContext, TTransformedValues>,
): UseFormReturn<TFieldValues, TContext, TTransformedValues> {
  const { unsavedChangesGuard, ...rhfProps } = props ?? {};

  const methods = useRHFForm<TFieldValues, TContext, TTransformedValues>({
    ...rhfProps,
    mode: rhfProps.mode ?? DEFAULT_VALIDATION_MODE,
  } as UseFormProps<TFieldValues, TContext, TTransformedValues>);

  // Synchronous flag for "mid-submit" - avoids a render-timing race where `navigate()` inside
  // onValid can run before the guard sees `isSubmitting` flip back to `false`.
  const isSubmitValidatedRef = useRef(false);
  const rhfHandleSubmit = methods.handleSubmit;

  const { isDirty, isSubmitting } = methods.formState;
  const guardEnabled = unsavedChangesGuard !== false && isDirty && !isSubmitting;

  const handleSubmit = useCallback<typeof methods.handleSubmit>(
    (onValid, onInvalid) =>
      rhfHandleSubmit(async (data, event) => {
        isSubmitValidatedRef.current = true;
        try {
          return await onValid(data, event);
        } finally {
          isSubmitValidatedRef.current = false;
        }
      }, onInvalid),
    [rhfHandleSubmit],
  );

  const shouldBlock = useCallback(
    () => guardEnabled && !isSubmitValidatedRef.current,
    [guardEnabled],
  );

  useUnsavedChangesGuard({
    enabled: guardEnabled,
    message: unsavedChangesGuard !== false ? unsavedChangesGuard?.message : undefined,
    shouldBlock,
  });

  // Use a ref to track methods and handleSubmit to avoid unnecessary re-renders when the form state changes
  const stableMethodsRef = useRef<UseFormReturn<TFieldValues, TContext, TTransformedValues>>(
    {} as UseFormReturn<TFieldValues, TContext, TTransformedValues>,
  );

  Object.assign(stableMethodsRef.current, methods, { handleSubmit });

  // A no-op unless an app has armed the registry — see `installDevFormRegistry`.
  useEffect(
    () => registerDevForm(stableMethodsRef.current as unknown as UseFormReturn<FieldValues>),
    [],
  );

  return stableMethodsRef.current;
}
